import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { setUser } from '../../store/slices/authSlice';
import { Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

const FormContainer = styled.div`
  background: rgba(20, 20, 20, 0.9);
  padding: 1.5rem;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1rem;
    margin: 0 1rem;
  }
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.75rem;
  font-weight: normal;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: white;
  font-size: 0.9rem;
  font-weight: normal;
`;

const Input = styled.input`
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #D4AF37;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  padding: 0.875rem;
  background: #D4AF37;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 0.5rem;
  -webkit-appearance: none;
  appearance: none;

  &:disabled {
    background: #8a7224;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #C5A028;
  }
`;

const LoginText = styled.div`
  color: white;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;

  a {
    color: #D4AF37;
    text-decoration: none;
    margin-left: 0.25rem;
    padding: 0.25rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

export const RegisterForm = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validateForm = () => {
    if (username.length < 3) {
      setError("Le nom d'utilisateur doit contenir au moins 3 caractères");
      return false;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError("L'adresse email n'est pas valide");
      return false;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Début de l\'inscription...');
      
      // Créer l'utilisateur dans Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('Utilisateur créé dans Firebase Auth:', user.uid);

      // Mettre à jour le profil de l'utilisateur avec son nom d'utilisateur
      await updateProfile(user, {
        displayName: username
      });
      console.log('Profil utilisateur mis à jour');

      // Créer le document utilisateur dans Firestore
      const userData = {
        uid: user.uid,
        email: email,
        displayName: username,
        username: username,
        photoURL: null,
        role: 'user' as const,
        recentTrailers: [],
        watchlist: { movies: [], tvShows: [] },
        favorites: [],
        history: [],
        settings: {
          darkMode: false,
          language: 'fr',
          notifications: true,
          autoplayTrailers: true,
          privacy: 'public' as const
        }
      };

      console.log('Tentative de création du document utilisateur dans Firestore...');
      await setDoc(doc(db, 'users', user.uid), userData);
      console.log('Document utilisateur créé dans Firestore');

      // Mettre à jour le state Redux
      dispatch(setUser(userData));
      console.log('State Redux mis à jour');
      
      navigate('/');
    } catch (error: any) {
      console.error('Erreur détaillée lors de l\'inscription:', error);
      console.error('Code d\'erreur:', error.code);
      console.error('Message d\'erreur:', error.message);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('Cette adresse email est déjà utilisée');
      } else {
        setError('Une erreur est survenue lors de l\'inscription. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <Title>Inscription</Title>
      <Form onSubmit={handleSubmit} noValidate>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <FormGroup>
          <Label htmlFor="username">Nom d'utilisateur</Label>
          <Input
            id="username"
            type="text"
            placeholder="Votre nom d'utilisateur"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            minLength={3}
            autoComplete="username"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Votre adresse email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="password">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            placeholder="Votre mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
          />
        </FormGroup>
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Inscription en cours...' : "S'inscrire"}
        </SubmitButton>
      </Form>
      <LoginText>
        Déjà un compte ? <Link to="/login">Se connecter</Link>
      </LoginText>
    </FormContainer>
  );
};
